import { useEffect, useRef, useState } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { toast } from "react-toastify";
import { z } from "zod";
import { HiOutlineBell, HiOutlineCheck } from "react-icons/hi";

import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { analytics, EventEnum } from "@/lib/analytics";

type PreReleaseNotifyFormProps = {
  albumId: string;
};

type NotifyState = "idle" | "input" | "submitting" | "confirmed";

const emailSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email." }),
});

const STORAGE_PREFIX = "prerelease-notify-";

function getInitialState(albumId: string): NotifyState {
  try {
    return sessionStorage.getItem(`${STORAGE_PREFIX}${albumId}`) === "confirmed"
      ? "confirmed"
      : "idle";
  } catch {
    return "idle";
  }
}

export default function PreReleaseNotifyForm({
  albumId,
}: PreReleaseNotifyFormProps) {
  const { data: user } = useLoggedInUser();
  const [state, setState] = useState<NotifyState>(() =>
    getInitialState(albumId),
  );
  const [email, setEmail] = useState("");
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLoggedIn = !!user?.userId;

  useEffect(() => {
    if (state === "input") {
      inputRef.current?.focus();
    }
  }, [state]);

  const submitNotify = (userEmail: string) => {
    setState("submitting");

    analytics.identify(user?.userId || null, {
      email: userEmail,
      prerelease_album_interest: true,
      album_id: albumId,
      source: "prerelease_cta",
    });

    analytics.track(EventEnum.PRERELEASE_NOTIFY, {
      album_id: albumId,
      source: "prerelease_cta",
      is_logged_in: !!user?.userId,
    });

    toast.success("We'll let you know when this album drops!", {
      autoClose: 5000,
    });

    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}${albumId}`, "confirmed");
    } catch {}

    setState("confirmed");
  };

  const handleGetNotified = () => {
    if (isLoggedIn && user?.email) {
      submitNotify(user.email);
    } else {
      setState("input");
    }
  };

  const handleSubmitEmail = () => {
    const trimmed = email.trim();
    const result = emailSchema.safeParse({ email: trimmed });
    if (!result.success) {
      setHasError(true);
      return;
    }
    setHasError(false);
    submitNotify(trimmed);
  };

  if (state === "confirmed") {
    return (
      <Flex align="center" className="gap-2 py-1" role="status">
        <HiOutlineCheck
          className="w-4 h-4 text-[#43DDA7]/70 shrink-0"
          aria-hidden="true"
        />
        <Text className="text-[14px] font-medium text-white/60">
          You'll be notified when this drops
        </Text>
      </Flex>
    );
  }

  if (state === "input") {
    return (
      <Flex direction="column" className="gap-3 w-full">
        <Text className="text-[12px] text-white/50 text-center">
          Enter your email to get notified when this drops
        </Text>
        <Flex className="gap-2 w-full">
          <input
            ref={inputRef}
            type="email"
            autoComplete="email"
            spellCheck={false}
            placeholder="your@email.com"
            aria-label="Email address for release notification"
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? "notify-email-error" : undefined}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (hasError) setHasError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmitEmail();
              if (e.key === "Escape") {
                setState("idle");
                setEmail("");
                setHasError(false);
              }
            }}
            className={`flex-1 h-10 rounded-full bg-white/10 border px-4 text-white text-[14px] placeholder:text-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-colors ${
              hasError
                ? "border-red-400 focus:border-red-400"
                : "border-white/20 focus:border-[#43DDA7]/60"
            }`}
          />
          <button
            type="button"
            onClick={handleSubmitEmail}
            className="h-10 px-5 rounded-full bg-[#43DDA7] text-black font-semibold text-[14px] hover:bg-[#65E3B7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors cursor-pointer shrink-0"
          >
            Notify Me
          </button>
        </Flex>
        {hasError && (
          <Text
            id="notify-email-error"
            className="text-[12px] text-red-400 text-center"
            role="alert"
          >
            Please enter a valid email
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <button
      type="button"
      onClick={handleGetNotified}
      disabled={state === "submitting"}
      className="inline-flex items-center justify-center gap-2 h-10 px-6 rounded-full bg-[#43DDA7] text-black font-semibold text-[14px] hover:bg-[#65E3B7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors cursor-pointer disabled:opacity-50"
    >
      <HiOutlineBell className="w-4 h-4" />
      Get Notified
    </button>
  );
}
